/**
 * Drive trash side page — soft-deleted folders + files (paginated, infinite scroll).
 * Empty trash is async (DriveJobs SQS); UI polls until `emptying` clears.
 */

import { h } from 'preact'
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import {
  createDriveI18n,
  deleteJson,
  fetchJson,
  formatBytes,
  formatDate,
  jsonUrl,
  lightboxProps,
  postJson
} from './side-utils.js'
import { empty_trash_drive_index_path } from '../../routes'
import { DriveSideChrome } from './DriveSideChrome.jsx'

const TRASH_PAGE_LIMIT = 200
const EMPTYING_POLL_MS = 4000

function trashListUrl(offset = 0) {
  const url = new URL(jsonUrl(), window.location.origin)
  url.searchParams.set('limit', String(TRASH_PAGE_LIMIT))
  url.searchParams.set('offset', String(offset))
  return url.pathname + url.search
}

function LoadMoreSentinel({ hasMore, loadingMore, onVisible }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !hasMore) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onVisible()
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, onVisible])

  if (!hasMore && !loadingMore) return null

  return (
    <div ref={ref} class="text-center py-3 text-muted">
      {loadingMore && (
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">…</span>
        </div>
      )}
    </div>
  )
}

export function TrashPage() {
  const i18n = useMemo(() => createDriveI18n(), [])
  const t = (key) => i18n.t(key)
  const [folders, setFolders] = useState([])
  const [files, setFiles] = useState([])
  const [canEmptyFlag, setCanEmptyFlag] = useState(false)
  const [canRestore, setCanRestore] = useState(true)
  const [emptying, setEmptying] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextOffset, setNextOffset] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const loadingMoreRef = useRef(false)

  const applyPage = useCallback((payload, { append }) => {
    const pageFolders = payload.folders || []
    const pageFiles = payload.files || []
    const pagination = payload.pagination || {}
    setFolders((prev) => (append ? [...prev, ...pageFolders] : pageFolders))
    setFiles((prev) => (append ? [...prev, ...pageFiles] : pageFiles))
    setCanEmptyFlag(!!payload.can_empty)
    setCanRestore(payload.can_restore !== false)
    setEmptying(Boolean(payload.emptying))
    setHasMore(Boolean(pagination.has_more))
    setNextOffset(pagination.next_offset ?? 0)
    setLoaded(true)
  }, [])

  const load = useCallback(async () => {
    setError(null)
    try {
      const payload = await fetchJson(trashListUrl(0))
      applyPage(payload, { append: false })
    } catch (e) {
      setError(e.message)
      setLoaded(true)
    }
  }, [applyPage])

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMoreRef.current) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    try {
      const payload = await fetchJson(trashListUrl(nextOffset))
      applyPage(payload, { append: true })
    } catch (e) {
      setError(e.message)
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }, [hasMore, nextOffset, applyPage])

  useEffect(() => {
    load()
  }, [load])

  // Poll while background empty-trash job holds the cache lock.
  useEffect(() => {
    if (!emptying) return undefined
    const id = window.setInterval(() => {
      load()
    }, EMPTYING_POLL_MS)
    return () => window.clearInterval(id)
  }, [emptying, load])

  const emptyTrash = useCallback(async () => {
    if (!window.confirm(i18n.t('drive.confirm_empty_trash'))) return
    setBusy(true)
    try {
      await deleteJson(empty_trash_drive_index_path({ format: 'json' }))
      setFolders([])
      setFiles([])
      setHasMore(false)
      setEmptying(true)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [i18n])

  const restoreFolder = useCallback(async (folder) => {
    setBusy(true)
    try {
      await postJson(folder.restore_url)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [load])

  const restoreFile = useCallback(async (file) => {
    setBusy(true)
    try {
      await postJson(file.restore_url)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [load])

  const permanentDelete = useCallback(async (file) => {
    if (!window.confirm(t('drive.confirm_permanent_delete'))) return
    setBusy(true)
    try {
      await deleteJson(file.permanent_delete_url)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }, [load, t])

  const empty = loaded && folders.length === 0 && files.length === 0 && !emptying
  const canEmpty = !!(canEmptyFlag && !empty && !emptying)
  const headerActions = canEmpty ? (
    <button type="button" class="btn btn-danger ms-2" disabled={busy || emptying} onClick={emptyTrash}>
      <i class="bi bi-trash3" />
      <span class="d-none d-lg-inline ms-1">{t('drive.empty_trash')}</span>
    </button>
  ) : null

  if (error && !loaded) {
    return (
      <DriveSideChrome titleKey="drive.trash" titleIcon="bi-trash">
        <div class="alert alert-danger">{error}</div>
      </DriveSideChrome>
    )
  }
  if (!loaded) {
    return (
      <DriveSideChrome titleKey="drive.trash" titleIcon="bi-trash">
        <div class="text-muted py-4 text-center">…</div>
      </DriveSideChrome>
    )
  }

  return (
    <DriveSideChrome titleKey="drive.trash" titleIcon="bi-trash" actions={headerActions}>
      <div class="drive-file-list">
        {error && <div class="alert alert-warning">{error}</div>}
        {emptying && (
          <div class="alert alert-info d-flex align-items-center gap-2">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">…</span>
            </div>
            <span>{t('drive.trash_emptying')}</span>
          </div>
        )}
        {empty ? (
          <div class="empty-state text-center py-5">
            <i class="bi bi-trash fs-1 text-muted mb-3" />
            <h5 class="text-muted">{t('drive.trash_empty')}</h5>
            <p class="text-muted">{t('drive.trash_empty_hint')}</p>
          </div>
        ) : emptying && folders.length === 0 && files.length === 0 ? (
          <div class="text-muted py-4 text-center">{t('drive.trash_emptying')}</div>
        ) : (
          <>
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th width="50" />
                    <th>{t('drive.name')}</th>
                    <th>{t('drive.deleted_at')}</th>
                    <th>{t('drive.deleted_by')}</th>
                    <th>{t('drive.file_size')}</th>
                    <th width="120" />
                  </tr>
                </thead>
                {folders.length > 0 && (
                  <tbody>
                    {folders.map((folder) => (
                      <tr key={`folder-${folder.id}`} class="drive-item" tabindex="0" role="row">
                        <td><i class="bi bi-folder text-muted" /></td>
                        <td><span class="text-dark fw-medium text-wrap text-break">{folder.name}</span></td>
                        <td class="text-muted">{formatDate(folder.deleted_at)}</td>
                        <td class="text-muted">{folder.deleted_by || t('drive.unknown')}</td>
                        <td class="text-muted">—</td>
                        <td>
                          {canRestore && !emptying && (
                            <div class="btn-group btn-group-sm" role="group">
                              <button
                                type="button"
                                class="btn btn-outline-success btn-sm"
                                disabled={busy}
                                onClick={() => restoreFolder(folder)}
                              >
                                <i class="bi bi-arrow-counterclockwise" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
                {files.length > 0 && (
                  <tbody>
                    {files.map((file) => (
                      <tr key={`file-${file.id}`} class="drive-item" tabindex="0" role="row">
                        <td><i class="bi bi-file-earmark text-muted" /></td>
                        <td>
                          <a
                            href={file.lightbox_url || '#'}
                            class="text-decoration-none text-dark fw-medium text-wrap text-break"
                            {...lightboxProps(file.lightbox)}
                          >
                            {file.filename}
                          </a>
                        </td>
                        <td class="text-muted">{formatDate(file.deleted_at)}</td>
                        <td class="text-muted">{file.deleted_by || t('drive.unknown')}</td>
                        <td class="text-muted">{formatBytes(file.byte_size)}</td>
                        <td>
                          <div class="btn-group btn-group-sm" role="group">
                            {canRestore && !emptying && (
                              <button
                                type="button"
                                class="btn btn-outline-success btn-sm"
                                disabled={busy}
                                onClick={() => restoreFile(file)}
                              >
                                <i class="bi bi-arrow-counterclockwise" />
                              </button>
                            )}
                            {!emptying && (
                              <button
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                disabled={busy}
                                onClick={() => permanentDelete(file)}
                              >
                                <i class="bi bi-x-circle" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
            <LoadMoreSentinel hasMore={hasMore && !emptying} loadingMore={loadingMore} onVisible={loadMore} />
          </>
        )}
      </div>
    </DriveSideChrome>
  )
}
